import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

import axios from 'axios';
import { API_BASE } from '../config';
import jsPDF from 'jspdf';
import { toPng } from 'html-to-image';
import Loading from './Loading';
import DeleteConfirmation from './DeleteConfirmation';
import ProductFlowDiagram from './ProductFlowDiagram';
import { useLanguage } from '../LanguageContext';
import translations from '../translations';

const ProductDetail = () => {
  const { t, isRtl } = useLanguage();
  const { uuid, workspaceUuid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dispatches, setDispatches] = useState([]);
  const [dispatchLoading, setDispatchLoading] = useState(false);
  const [wasteProducts, setWasteProducts] = useState([]);
  const [wasteLoading, setWasteLoading] = useState(false);
  const [downstreamProducts, setDownstreamProducts] = useState([]);
  const [downstreamLoading, setDownstreamLoading] = useState(false);
  const [usedProductsLoading, setUsedProductsLoading] = useState(false);
  const [wasteMenuOpenId, setWasteMenuOpenId] = useState(null);
  const [wasteMenuPos, setWasteMenuPos] = useState({ x: 0, y: 0 });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, name: '' });
  const [overviewOpen, setOverviewOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showExportConfirm, setShowExportConfirm] = useState(false);
  const [editWasteModal, setEditWasteModal] = useState(false);
  const [wasteEditForm, setWasteEditForm] = useState({ id: '', type: '', quantity: '', name: '', comment: '', date: '', time: '' });
  const [groupOverview, setGroupOverview] = useState(null);
  const [groupOverviewOpen, setGroupOverviewOpen] = useState(false);
  const [transitionLoading, setTransitionLoading] = useState(false);
  const diagramRef = useRef(null);
  const groupDiagramRef = useRef(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleExportPDF = async () => {
    if (!product) return;
    setExportLoading(true);
    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pdfT = translations.english;
      const pdfIsRtl = false;
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 15;
      let y = 20;

      // --- Title ---
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(22);
      doc.text(String(product.product_name), pdfIsRtl ? pageWidth - margin : margin, y, { align: pdfIsRtl ? 'right' : 'left' });
      y += 8;

      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100);
      doc.text(String(`${pdfT.product_code || "Product Code"}: #${product.product_code}`), pdfIsRtl ? pageWidth - margin : margin, y, { align: pdfIsRtl ? 'right' : 'left' });
      y += 12;

      // --- General Info ---
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(0);
      doc.text(String(pdfT.general_information || "General Information"), pdfIsRtl ? pageWidth - margin : margin, y, { align: pdfIsRtl ? 'right' : 'left' });
      y += 8;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const info = [
        [`${pdfT.stock_quantity || "Stock Quantity"}:`, `${product.product_quantiry || 0} ${pdfT.kg || "kg"}`],
        [`${pdfT.manufacture_date || "Manufacture Date"}:`, formatDate(product.manufacture_date)],
        [`${pdfT.export_date || "Export Date"}:`, new Date().toLocaleDateString(pdfIsRtl ? 'ar-EG' : 'en-US')]
      ];

      info.forEach(([label, value]) => {
        doc.setFont('helvetica', 'bold');
        doc.text(String(label), pdfIsRtl ? pageWidth - margin : margin, y, { align: pdfIsRtl ? 'right' : 'left' });
        doc.setFont('helvetica', 'normal');
        doc.text(String(value), pdfIsRtl ? pageWidth - margin - 50 : margin + 50, y, { align: pdfIsRtl ? 'right' : 'left' });
        y += 6;
      });
      y += 10;

      // --- Raw Materials ---
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.text(String(pdfT.ingredients_used || "Ingredients Used"), pdfIsRtl ? pageWidth - margin : margin, y, { align: pdfIsRtl ? 'right' : 'left' });
      y += 8;

      const ingredients = [
        ...(product.used_raw_materials || []).map(u => ({
          name: u.rawMaterialId?.material_name || u.rawMaterialName || '-',
          batch: u.batch_number || '-',
          code: u.rawMaterialId?.material_code || '-',
          qty: `${u.quantity || 0} kg`
        })),
        ...(product.used_products || []).map(p => ({
          name: p.productId?.product_name || '-',
          batch: p.productId?.batch || '-',
          code: p.productId?.product_code || '-',
          qty: `${p.quantity || 0} kg`
        }))
      ];

      const headers = [pdfT.ingredient, pdfT.batch, pdfT.code, pdfT.qty_used];
      const data = ingredients.map(i => [i.name, i.batch, i.code, i.qty]);

      let startX = margin;
      let cellWidth = (pageWidth - margin * 2) / 4;

      doc.setFontSize(10);
      headers.forEach((h, i) => {
        doc.setFillColor(240, 240, 240);
        doc.rect(startX + i * cellWidth, y - 5, cellWidth, 7, 'F');
        doc.text(h, startX + i * cellWidth + 2, y);
      });
      y += 7;

      data.forEach(row => {
        if (y > 270) { doc.addPage(); y = 20; }
        row.forEach((cell, i) => {
          doc.text(String(cell), startX + i * cellWidth + 2, y);
        });
        y += 6;
      });
      y += 15;

      // --- Dispatches ---
      if (dispatches.length > 0) {
        if (y > 250) { doc.addPage(); y = 20; }
        doc.text(String(pdfT.recent_dispatches || 'Recent Dispatches'), margin, y);
        y += 8;

        dispatches.slice(0, 10).forEach(d => {
          const pInD = (d.products || []).find(p => p.productId?._id === product._id || p.productId === product._id);
          const line = `${d.po_number || d.dispatchId || d.uuid} - ${pInD?.quantity || 0} ${pdfT.kg || "kg"} ${pdfT.to || "to"} ${d.customerId?.customer_name || '-'} ${pdfT.on || "on"} ${formatDate(d.dispatch_date)}`;
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(10);
          doc.text(String(line), pdfIsRtl ? pageWidth - margin : margin, y, { align: pdfIsRtl ? 'right' : 'left' });
          y += 6;
          if (y > 280) { doc.addPage(); y = 20; }
        });
      }

      // --- Page 2: Product Lineage Diagram ---
      if (diagramRef.current) {
        // Wait for rendering transition/data
        await new Promise(r => setTimeout(r, 800));

        // Explicitly set width to avoid layout shifting during capture
        const captureWidth = 1400;

        const imgData = await toPng(diagramRef.current, {
          quality: 1.0,
          pixelRatio: 3, // Premium clarity
          backgroundColor: '#fafbfc',
          width: captureWidth,
          skipFonts: true,
          style: {
            fontFamily: "'Outfit', -apple-system, system-ui, sans-serif",
            fontWeight: '400'
          }
        });

        doc.addPage();
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(16);
        doc.text(String(pdfT.product_lineage_overview || ""), pdfIsRtl ? pageWidth - margin : margin, 20, { align: pdfIsRtl ? 'right' : 'left' });

        // Calculate aspect ratio to prevent distortion
        const originalHeight = diagramRef.current.offsetHeight;
        const originalWidth = diagramRef.current.offsetWidth;
        const aspectRatio = originalHeight / originalWidth;

        const pdfImgWidth = pageWidth - margin * 2;
        const pdfImgHeight = pdfImgWidth * aspectRatio;

        doc.addImage(imgData, 'PNG', margin, 35, pdfImgWidth, pdfImgHeight);
      }

      // --- Page 3: Group Production Flow (Optional) ---
      if (groupOverview && groupDiagramRef.current) {
        await new Promise(r => setTimeout(r, 800));
        const captureWidth = 1400;
        const groupImgData = await toPng(groupDiagramRef.current, {
          quality: 1.0,
          pixelRatio: 3,
          backgroundColor: '#fafbfc',
          width: captureWidth,
          skipFonts: true,
          style: {
            fontFamily: "'Outfit', -apple-system, system-ui, sans-serif",
            fontWeight: '400'
          }
        });

        doc.addPage();
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(16);
        doc.text(String(pdfT.group_production_overview || ""), pdfIsRtl ? pageWidth - margin : margin, 20, { align: pdfIsRtl ? 'right' : 'left' });

        const groupOriginalHeight = groupDiagramRef.current.offsetHeight;
        const groupOriginalWidth = groupDiagramRef.current.offsetWidth;
        const groupAspectRatio = groupOriginalHeight / groupOriginalWidth;

        const groupPdfImgWidth = pageWidth - margin * 2;
        const groupPdfImgHeight = groupPdfImgWidth * groupAspectRatio;

        doc.addImage(groupImgData, 'PNG', margin, 35, groupPdfImgWidth, groupPdfImgHeight);
      }

      doc.save(`${product.product_name}_Report.pdf`);
      setShowExportConfirm(false);
    } catch (err) {
      console.error('PDF Export Error:', err);
      alert(t.fetch_failed);
    } finally {
      setExportLoading(false);
    }
  };

  const fetchWaste = async (pId) => {
    if (!pId) return;
    setWasteLoading(true);
    try {
      const wRes = await axios.get(`${API_BASE}/wasteproducts/product/${pId}`, { headers: getAuthHeaders() });
      setWasteProducts(wRes.data || []);
    } catch (err) {
      console.error('Failed to fetch waste products:', err);
    } finally {
      setWasteLoading(false);
    }
  };

  useEffect(() => {
    const onDocClick = (e) => {
      if (!e.target.closest('.waste-floating-menu') && !e.target.closest('.waste-menu-btn')) {
        setWasteMenuOpenId(null);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  const fetchProductData = async () => {
    try {
      const res = await axios.get(`${API_BASE}/products`, { headers: getAuthHeaders() });
      const found = (res.data || []).find(p => p.uuid === uuid);
      if (!found) {
        alert(t.no_products_found);
        navigate(`/workspace/${workspaceUuid}/products`);
        return;
      }
      setProduct(found);
      setUsedProductsLoading(true);
      fetchWaste(found._id);

      // Fetch group overview if this product belongs to a group
      if (found.groupId) {
        try {
          const gRes = await axios.get(`${API_BASE}/group-products/by-product/${found._id}`, { headers: getAuthHeaders() });
          setGroupOverview(gRes.data);
        } catch (err) {
          // Not a group product or group was deleted — ignore
          setGroupOverview(null);
        }
      }

      // Find downstream products
      setDownstreamLoading(true);
      const allProds = res.data || [];
      const related = allProds.filter(p =>
        (p.used_products || []).some(up => up.productId?._id === found._id || up.productId === found._id)
      );
      setDownstreamProducts(related);
      setDownstreamLoading(false);

      if (found.dispatchIds && found.dispatchIds.length > 0) {
        setDispatchLoading(true);
        try {
          const dRes = await axios.get(`${API_BASE}/dispatchedproducts`, { headers: getAuthHeaders() });
          const relatedDispatches = (dRes.data || []).filter(d => found.dispatchIds.includes(d._id));
          setDispatches(relatedDispatches);
        } catch (err) {
          console.error('Failed to fetch dispatches:', err);
        } finally {
          setDispatchLoading(false);
        }
      }
      setUsedProductsLoading(false);
    } catch (err) {
      console.error(err);
      alert(t.fetch_failed);
      navigate(`/workspace/${workspaceUuid}/products`);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    setLoading(true); // Ensure loading is reset on uuid change
    setProduct(null); // Clear previous data
    fetchProductData();
  }, [uuid]);


  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('autoOpenOverview') === 'true') {
      setOverviewOpen(true);
      // Clean up URL without triggering re-fetch if possible, or just replace
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);


  const isAutoOpening = new URLSearchParams(location.search).get('autoOpenOverview') === 'true';

  if (loading || transitionLoading) {
    const msg = transitionLoading ? t.switching_views : (isAutoOpening ? t.preparing_overview : t.fetching_product_details);
    return <Loading type="full" message={msg} />;
  }

  if (!product) return null;

  const formatDate = d => d ? new Date(d).toLocaleDateString(isRtl ? 'ar-EG' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';
  // Display helper: round to 2dp for display
  const fmt = (v) => parseFloat(Number(v || 0).toFixed(2));

  const handleWasteDelete = (id) => {
    const record = wasteProducts.find(w => w._id === id);
    setDeleteConfirm({ open: true, id, name: `${record?.type || 'record'} (${record?.quantity || 0} kg)`, type: 'waste' });
  };


  const openWasteEdit = (w) => {
    setWasteEditForm({
      id: w._id,
      type: w.type,
      quantity: String(w.quantity),
      name: w.name,
      comment: w.comment || '',
      date: w.date ? new Date(w.date).toISOString().slice(0, 10) : '',
      time: w.time || ''
    });
    setEditWasteModal(true);
  };

  const handleShowGroupOverview = () => {
    if (groupOverview) {
      setTransitionLoading(true);
      setTimeout(() => {
        setGroupOverviewOpen(true);
        setOverviewOpen(false);
        setTransitionLoading(false);
      }, 600);
    }
  };

  const handleShowSimpleOverview = (targetUuid) => {
    setTransitionLoading(true);
    setTimeout(() => {
      setGroupOverviewOpen(false);
      setOverviewOpen(true);
      setTransitionLoading(false);
    }, 600);
  };

  const handleWasteEditChange = (e) => {
    const { name, value } = e.target;
    setWasteEditForm(prev => ({ ...prev, [name]: value }));
  };

  const handleWasteEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_BASE}/wasteproducts/${wasteEditForm.id}`, {
        type: wasteEditForm.type,
        quantity: Number(wasteEditForm.quantity),
        name: wasteEditForm.name,
        comment: wasteEditForm.comment,
        date: wasteEditForm.date,
        time: wasteEditForm.time
      }, { headers: getAuthHeaders() });
      setEditWasteModal(false);
      fetchProductData();
    } catch (err) { alert(err.response?.data?.msg || t.save_failed); }
  };

  const executeDelete = async () => {
    try {
      if (deleteConfirm.type === 'waste') {
        await axios.delete(`${API_BASE}/wasteproducts/${deleteConfirm.id}`, { headers: getAuthHeaders() });
      } else {
        // assume product delete if no type (legacy) or handle as needed
        await axios.delete(`${API_BASE}/products/${deleteConfirm.id}`, { headers: getAuthHeaders() });
      }
      setDeleteConfirm({ open: false, id: null, name: '', type: '' });
      fetchProductData();
    } catch (err) {
      alert(err.response?.data?.msg || t.delete_failed);
      setDeleteConfirm({ open: false, id: null, name: '' });
    }
  };

  return (
    <>
      <div className="fade-in" style={{ maxWidth: 1000, margin: '0 auto', paddingBottom: 60 }}>
        {/* Hidden container for PDF export capture */}
        <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', overflow: 'visible', direction: 'ltr' }}>
          <div ref={diagramRef} style={{ width: 1400, padding: 60, background: '#fafbfc', minHeight: 800 }}>
            <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 20, textAlign: 'center' }}>{translations.english.manufacturing_lineage_diagram}</h2>
            <ProductFlowDiagram
              product={product}
              wasteProducts={wasteProducts}
              dispatches={dispatches}
              downstreamProducts={downstreamProducts}
              currentUuid={product?.uuid}
              forceT={translations.english}
              forceIsRtl={false}
            />

          </div>
          {groupOverview && (
            <div ref={groupDiagramRef} style={{ width: 1400, padding: 60, background: '#fafbfc', minHeight: 800 }}>
              <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 20, textAlign: 'center' }}>{translations.english.group_production_flow}</h2>
              <ProductFlowDiagram
                product={{
                  product_name: 'Group Batch',
                  used_raw_materials: groupOverview.used_raw_materials || [],
                  used_products: groupOverview.used_products || [],
                  product_quantiry: groupOverview.total_output_qty,
                  quantityProduced: groupOverview.total_ingredients_qty
                }}
                groupProducts={groupOverview.productIds || []}
                wasteProducts={groupOverview.wasteProductIds || []}
                currentUuid={product?.uuid}
                forceT={translations.english}
                forceIsRtl={false}
              />

            </div>
          )}
        </div>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
            <button
              onClick={() => navigate(`/workspace/${workspaceUuid}/products`)}
              style={{
                background: 'none', border: '1px solid #d2d2d2', borderRadius: 6, padding: '6px 8px',
                cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#6d7175', transform: isRtl ? 'rotate(180deg)' : 'none'
              }}
            >
              <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </button>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#1a1c1d', textAlign: isRtl ? 'right' : 'left' }}>
              {product.product_name}
              <span style={{ color: '#6d7175', fontSize: 14, fontWeight: 500, [isRtl ? 'marginRight' : 'marginLeft']: 10 }}>#{product.product_code}</span>
            </h1>
          </div>
          <div style={{ display: 'flex', gap: 8, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
            <button
              onClick={() => setShowExportConfirm(true)}
              disabled={exportLoading}
              style={{
                padding: '8px 14px', borderRadius: 6, border: '1px solid #d2d2d2',
                background: '#fff', color: '#1a1c1d', fontWeight: 600, fontSize: 13,
                cursor: exportLoading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                opacity: exportLoading ? 0.7 : 1, minWidth: 105, justifyContent: 'center',
                flexDirection: isRtl ? 'row-reverse' : 'row'
              }}
            >
              {exportLoading ? (
                <div className="dots-loading">
                  <span style={{ backgroundColor: '#1976d2' }}></span>
                  <span style={{ backgroundColor: '#1976d2' }}></span>
                  <span style={{ backgroundColor: '#1976d2' }}></span>
                </div>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: isRtl ? 'scaleX(-1)' : 'none' }}>
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  {t.export_btn}
                </>
              )}
            </button>
            {groupOverview && (
              <button onClick={() => setGroupOverviewOpen(true)} style={{
                padding: '8px 14px', borderRadius: 6, border: 'none',
                background: 'linear-gradient(135deg, #1976D2, #1565C0)', color: '#fff', fontWeight: 600, fontSize: 13,
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                boxShadow: '0 2px 8px rgba(25,118,210,0.25)', transition: 'all 0.2s'
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="#fff" strokeWidth="1.5" strokeLinejoin="round" />
                  <path d="M2 12l10 5 10-5" stroke="#fff" strokeWidth="1.5" strokeLinejoin="round" />
                  <path d="M2 17l10 5 10-5" stroke="#fff" strokeWidth="1.5" strokeLinejoin="round" />
                </svg>
                {t.group_products}
              </button>
            )}
            <button onClick={() => setOverviewOpen(true)} style={{
              padding: '8px 14px', borderRadius: 6, border: 'none',
              background: 'linear-gradient(135deg, #1976D2, #1565C0)', color: '#fff', fontWeight: 600, fontSize: 13,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
              boxShadow: '0 2px 8px rgba(25,118,210,0.25)', transition: 'all 0.2s'
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ transform: isRtl ? 'scaleX(-1)' : 'none' }}>
                <circle cx="12" cy="5" r="3" stroke="#fff" strokeWidth="1.5" />
                <circle cx="5" cy="19" r="3" stroke="#fff" strokeWidth="1.5" />
                <circle cx="19" cy="19" r="3" stroke="#fff" strokeWidth="1.5" />
                <line x1="12" y1="8" x2="5" y2="16" stroke="#fff" strokeWidth="1.5" />
                <line x1="12" y1="8" x2="19" y2="16" stroke="#fff" strokeWidth="1.5" />
              </svg>
              {t.see_overview}
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', gap: 20, alignItems: 'start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* General Information Card */}
            <section style={{
              background: '#fff', padding: 20, borderRadius: 12,
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', border: '1px solid #ebebeb',
              direction: isRtl ? 'rtl' : 'ltr', textAlign: isRtl ? 'right' : 'left'
            }}>
              <h3 style={{ marginTop: 0, marginBottom: 16, color: '#1a1c1d', fontSize: 14, fontWeight: 600 }}>{t.general_information}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <div style={{ fontSize: 12, color: '#6d7175', marginBottom: 4 }}>{t.product_name}</div>
                  <div style={{ fontWeight: 500, color: '#1a1c1d', fontSize: 14 }}>{product.product_name}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#6d7175', marginBottom: 4 }}>{t.product_code}</div>
                  <div style={{ fontWeight: 500, color: '#1a1c1d', fontSize: 14 }}>{product.product_code}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#6d7175', marginBottom: 4 }}>{t.quantity_produced}</div>
                  <div style={{ fontWeight: 600, color: '#1a1c1d', fontSize: 14, textAlign: isRtl ? 'right' : 'left' }}>{fmt(product.quantityProduced ?? product.product_quantiry)} {t.kg}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#6d7175', marginBottom: 4 }}>{t.remaining_in_stock}</div>
                  <div style={{ fontWeight: 600, color: '#1976d2', fontSize: 14, textAlign: isRtl ? 'right' : 'left' }}>{fmt(product.product_quantiry)} {t.kg}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#6d7175', marginBottom: 4 }}>{t.manufactured_date}</div>
                  <div style={{ fontWeight: 500, color: '#1a1c1d', fontSize: 14 }}>{formatDate(product.manufacture_date)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#6d7175', marginBottom: 4 }}>{t.batch_number}</div>
                  <div style={{ fontWeight: 500, color: '#1a1c1d', fontSize: 14 }}>{product.batch || '-'}</div>
                </div>
              </div>
            </section>

            {/* Ingredients Section (Combined Raw Materials + Sub-products) */}
            <section style={{
              background: '#fff', borderRadius: 12,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #ebebeb',
              overflow: 'hidden'
            }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f1f1', textAlign: isRtl ? 'right' : 'left' }}>
                <h3 style={{ margin: 0, color: '#1a1c1d', fontSize: 14, fontWeight: 600 }}>{t.ingredients_used}</h3>
              </div>
              <div style={{ overflowX: 'auto', direction: isRtl ? 'rtl' : 'ltr' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, color: '#1a1c1d' }}>
                  <thead>
                    <tr style={{ background: '#f9fafb', borderBottom: '1px solid #ebebeb' }}>
                      <th style={{ width: 50 }}></th>
                      <th style={{ padding: '10px 20px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{t.ingredient}</th>
                      <th style={{ padding: '10px 20px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{t.batch}</th>
                      <th style={{ padding: '10px 20px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{t.code}</th>
                      <th style={{ padding: '10px 20px', fontWeight: 600, textAlign: isRtl ? 'left' : 'right' }}>{t.qty_used}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {((product.used_raw_materials || []).length === 0 && (product.used_products || []).length === 0) ? (
                      <tr><td colSpan="5" style={{ padding: 24, textAlign: 'center', color: '#6d7175' }}>{t.no_ingredients_used}</td></tr>
                    ) : (
                      <>
                        {(product.used_raw_materials || []).map((u, i) => (
                          <tr key={`rm-${i}`} style={{ borderBottom: '1px solid #f1f1f1' }}>
                            <td style={{ textAlign: 'center', padding: '12px 0' }}>
                              <button
                                onClick={() => navigate(`/workspace/${workspaceUuid}/raw-materials/${u.rawMaterialId?.uuid || u.uuid}`)}
                                style={{
                                  background: 'none', border: 'none', cursor: 'pointer',
                                  color: '#2c6ecb', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  margin: '0 auto', padding: 4, borderRadius: 4
                                }}
                                onMouseOver={e => e.currentTarget.style.background = '#eef4ff'}
                                onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                                title={t.view_details}
                              >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
                              </button>
                            </td>
                            <td style={{ padding: '12px 20px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{u.rawMaterialId?.material_name || u.rawMaterialName}</td>
                            <td style={{ padding: '12px 20px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{u.batch_number}</td>
                            <td style={{ padding: '12px 20px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{u.rawMaterialId?.material_code || '-'}</td>
                            <td style={{ padding: '12px 20px', textAlign: isRtl ? 'left' : 'right', fontWeight: 600, color: '#1976d2' }}>{Number(u.quantity || 0).toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 2 })} {t.kg}</td>
                          </tr>
                        ))}
                        {(product.used_products || []).map((u, i) => (
                          <tr key={`p-${i}`} style={{ borderBottom: '1px solid #f1f1f1' }}>
                            <td style={{ textAlign: 'center', padding: '12px 0' }}>
                              <button
                                onClick={() => navigate(`/workspace/${workspaceUuid}/products/${u.productId?.uuid || u.uuid}`)}
                                style={{
                                  background: 'none', border: 'none', cursor: 'pointer',
                                  color: '#2c6ecb', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  margin: '0 auto', padding: 4, borderRadius: 4
                                }}
                                onMouseOver={e => e.currentTarget.style.background = '#eef4ff'}
                                onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                                title={t.view_details}
                              >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
                              </button>
                            </td>
                            <td style={{ padding: '12px 20px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{u.productId?.product_name || '-'}</td>
                            <td style={{ padding: '12px 20px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{u.productId?.batch || '-'}</td>
                            <td style={{ padding: '12px 20px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{u.productId?.product_code || '-'}</td>
                            <td style={{ padding: '12px 20px', textAlign: isRtl ? 'left' : 'right', fontWeight: 600, color: '#1976d2' }}>{Number(u.quantity || 0).toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 2 })} {t.kg}</td>
                          </tr>
                        ))}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Waste/Tested Records Section (Only for non-group products with data) */}
            {!product.groupId && wasteProducts.length > 0 && (
              <section className="fade-in" style={{
                background: '#fff', borderRadius: 12,
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #ebebeb',
                overflow: 'hidden', marginTop: 20
              }}>
                <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f1f1', textAlign: isRtl ? 'right' : 'left' }}>
                  <h3 style={{ margin: 0, color: '#1a1c1d', fontSize: 14, fontWeight: 600 }}>{t.waste_records}</h3>
                </div>
                <div style={{ overflowX: 'auto', direction: isRtl ? 'rtl' : 'ltr' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, color: '#1a1c1d' }}>
                    <thead>
                      <tr style={{ background: '#f9fafb', borderBottom: '1px solid #ebebeb' }}>
                        <th style={{ padding: '10px 20px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{t.date}</th>
                        <th style={{ padding: '10px 20px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{t.type}</th>
                        <th style={{ padding: '10px 20px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{t.person}</th>
                        <th style={{ padding: '10px 20px', fontWeight: 600, textAlign: isRtl ? 'left' : 'right' }}>{t.quantity} ({t.kg})</th>
                        <th style={{ padding: '10px 20px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{t.comment}</th>
                        <th style={{ padding: '10px 20px', fontWeight: 600, textAlign: isRtl ? 'left' : 'right' }}>{t.actions}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {wasteProducts.map((w) => (
                        <tr key={w._id} style={{ borderBottom: '1px solid #f1f1f1' }}>
                          <td style={{ padding: '12px 20px', textAlign: isRtl ? 'right' : 'left' }}>{new Date(w.date || w.createdAt).toLocaleDateString(isRtl ? 'ar-EG' : undefined)}</td>
                          <td style={{ padding: '12px 20px', textAlign: isRtl ? 'right' : 'left' }}>
                            <span style={{
                              padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                              background: w.type === 'waste' ? '#fff1f0' : '#e6f7ff',
                              color: w.type === 'waste' ? '#cf1322' : '#096dd9'
                            }}>
                              {w.type === 'waste' ? t.waste : (w.type === 'tested' ? t.tested : w.type)}
                            </span>
                          </td>
                          <td style={{ padding: '12px 20px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{w.name || w.personName || '-'}</td>
                          <td style={{ padding: '12px 20px', textAlign: isRtl ? 'left' : 'right', fontWeight: 600 }}>{Number(w.quantity || 0).toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 2 })} {t.kg}</td>
                          <td style={{ padding: '12px 20px', color: '#6d7175', fontSize: 12, textAlign: isRtl ? 'right' : 'left' }}>{w.comment || '-'}</td>
                          <td style={{ padding: '12px 20px', textAlign: isRtl ? 'left' : 'right' }}>
                            <button onClick={(e) => {
                              e.stopPropagation();
                              const rect = e.currentTarget.getBoundingClientRect();
                              setWasteMenuPos({ x: isRtl ? rect.right : rect.left, y: rect.bottom + 8 });
                              setWasteMenuOpenId(wasteMenuOpenId === w._id ? null : w._id);
                            }} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#6d7175', padding: 4 }}>
                              <svg width="20" height="20" viewBox="0 0 20 20"><path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM18 10a2 2 0 11-4 0 2 2 0 014 0z" fill="currentColor" /></svg>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {/* Product Used in Other Products (Downstream) */}
            {downstreamProducts.length > 0 && (
              <section className="fade-in" style={{
                background: '#fff', borderRadius: 12,
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #ebebeb',
                overflow: 'hidden', marginTop: 20
              }}>
                <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f1f1', textAlign: isRtl ? 'right' : 'left' }}>
                  <h3 style={{ margin: 0, color: '#1a1c1d', fontSize: 14, fontWeight: 600 }}>{t.product_used_to_make_products}</h3>
                </div>
                <div style={{ overflowX: 'auto', direction: isRtl ? 'rtl' : 'ltr' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, color: '#1a1c1d' }}>
                    <thead>
                      <tr style={{ background: '#f9fafb', borderBottom: '1px solid #ebebeb' }}>
                        <th style={{ width: 50 }}></th>
                        <th style={{ padding: '10px 20px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{t.product_name}</th>
                        <th style={{ padding: '10px 20px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{t.code}</th>
                        <th style={{ padding: '10px 20px', fontWeight: 600, textAlign: isRtl ? 'left' : 'right' }}>{t.consumed_qty}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {downstreamLoading ? (
                        <tr><td colSpan="4" style={{ padding: 24, textAlign: 'center' }}><Loading message={t.checking_downstream || "Checking downstream..."} /></td></tr>
                      ) : (
                        downstreamProducts.map((dp, idx) => {
                          const consumed = dp.used_products.find(up => up.productId?._id === product._id || up.productId === product._id);
                          return (
                            <tr key={idx} style={{ borderBottom: '1px solid #f1f1f1' }}>
                              <td style={{ textAlign: 'center', padding: '12px 0' }}>
                                <button
                                  onClick={() => navigate(`/workspace/${workspaceUuid}/products/${dp.uuid}`)}
                                  style={{
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    color: '#2c6ecb', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    margin: '0 auto', padding: 4, borderRadius: 4, transform: isRtl ? 'scaleX(-1)' : 'none'
                                  }}
                                  onMouseOver={e => e.currentTarget.style.background = '#eef4ff'}
                                  onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                                  title={t.view_details}
                                >
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
                                </button>
                              </td>
                              <td style={{ padding: '12px 20px', fontWeight: 600, color: '#1a1c1d', textAlign: isRtl ? 'right' : 'left' }}>{dp.product_name}</td>
                              <td style={{ padding: '12px 20px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{dp.product_code}</td>
                              <td style={{ padding: '12px 20px', textAlign: isRtl ? 'left' : 'right' }}>{Number(consumed?.quantity || 0).toLocaleString(isRtl ? 'ar-EG' : undefined)} {t.kg}</td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Dispatch History Card */}
            <section className="fade-in" style={{
              background: '#fff', borderRadius: 12,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #ebebeb',
              overflow: 'hidden'
            }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f1f1', textAlign: isRtl ? 'right' : 'left' }}>
                <h3 style={{ margin: 0, color: '#1a1c1d', fontSize: 14, fontWeight: 600 }}>{t.dispatch_history}</h3>
              </div>
              {dispatchLoading ? (
                <Loading message={t.loading_history} />
              ) : dispatches.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: '#6d7175', fontSize: 13 }}>{t.no_dispatch_history}</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {dispatches.map(d => {
                    const productInDispatch = (d.products || []).find(p => p.productId?._id === product._id || p.productId === product._id);
                    return (
                      <div key={d._id} style={{ padding: 16, borderBottom: '1px solid #f1f1f1', direction: isRtl ? 'rtl' : 'ltr', textAlign: isRtl ? 'right' : 'left' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                          <span style={{ fontWeight: 600, color: '#1a1c1d' }}>{d.po_number || d.dispatchId || d.uuid}</span>
                          <span style={{ fontWeight: 600, color: '#1976d2' }}>{Number(fmt(productInDispatch?.quantity)).toLocaleString(isRtl ? 'ar-EG' : undefined)} {t.kg}</span>
                        </div>
                        <div style={{ fontSize: 12, color: '#6d7175' }}>{d.customerId?.customer_name || d.customerId?.name || '-'}</div>
                        <div style={{ fontSize: 12, color: '#6d7175' }}>{formatDate(d.dispatch_date)}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        </div>
      </div>

      {/* Floating Menu Outside fade-in for centering */}
      {wasteMenuOpenId && (
        <div
          className="waste-floating-menu"
          style={{
            position: 'fixed',
            [isRtl ? 'right' : 'left']: isRtl ? (window.innerWidth - wasteMenuPos.x) : (wasteMenuPos.x - 120),
            top: wasteMenuPos.y, background: '#fff',
            borderRadius: 8, boxShadow: '0 12px 30px rgba(0,0,0,0.15)', minWidth: 160, zIndex: 2000,
            border: '1px solid #ebebeb'
          }} onClick={(e) => e.stopPropagation()}
        >
          <div style={{ display: 'flex', flexDirection: 'column', padding: 4, direction: isRtl ? 'rtl' : 'ltr' }}>
            <button onClick={() => { const w = wasteProducts.find(x => x._id === wasteMenuOpenId); if (w) openWasteEdit(w); setWasteMenuOpenId(null); }} style={{
              padding: '8px 12px', textAlign: isRtl ? 'right' : 'left', border: 'none', background: 'transparent',
              cursor: 'pointer', color: '#1a1c1d', fontSize: 13, borderRadius: 4, display: 'flex', alignItems: 'center', gap: 8, flexDirection: isRtl ? 'row-reverse' : 'row'
            }} onMouseOver={e => e.target.style.background = '#f1f1f1'} onMouseOut={e => e.target.style.background = 'transparent'}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4L18.5 2.5z"></path></svg>
              {t.edit_btn}
            </button>
            <button onClick={() => { setWasteMenuOpenId(null); handleWasteDelete(wasteMenuOpenId); }} style={{
              padding: '8px 12px', textAlign: isRtl ? 'right' : 'left', border: 'none', background: 'transparent',
              cursor: 'pointer', color: '#d82c0d', fontSize: 13, borderRadius: 4, display: 'flex', alignItems: 'center', gap: 8, flexDirection: isRtl ? 'row-reverse' : 'row'
            }} onMouseOver={e => e.target.style.background = '#f1f1f1'} onMouseOut={e => e.target.style.background = 'transparent'}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
              {t.delete_btn}
            </button>
          </div>
        </div>
      )}

      {/* Modals Outside fade-in for centering */}
      {editWasteModal && (
        <div className="overlay-fade-in" style={{
          position: 'fixed', inset: 0, background: 'rgba(26, 28, 29, 0.45)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1300,
          backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
          direction: isRtl ? 'rtl' : 'ltr'
        }}>
          <div className="modal-fade-in" style={{ width: 450, background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 20px 40px rgba(0,0,0,0.2)', textAlign: isRtl ? 'right' : 'left' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: 18, fontWeight: 700 }}>{t.edit_waste_record}</h3>
            <form onSubmit={handleWasteEditSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#6d7175', marginBottom: 4, textAlign: isRtl ? 'right' : 'left' }}>{t.type}</label>
                  <select name="type" value={wasteEditForm.type} onChange={handleWasteEditChange} style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #d2d2d2', textAlign: isRtl ? 'right' : 'left' }}>
                    <option value="waste">{t.waste}</option>
                    <option value="tested">{t.tested}</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#6d7175', marginBottom: 4, textAlign: isRtl ? 'right' : 'left' }}>{t.quantity}</label>
                  <input type="number" name="quantity" value={wasteEditForm.quantity} onChange={handleWasteEditChange} style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #d2d2d2', textAlign: isRtl ? 'right' : 'left' }} required />
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#6d7175', marginBottom: 4, textAlign: isRtl ? 'right' : 'left' }}>{t.person_name}</label>
                <input type="text" name="name" value={wasteEditForm.name} onChange={handleWasteEditChange} style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #d2d2d2', textAlign: isRtl ? 'right' : 'left' }} required />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#6d7175', marginBottom: 4, textAlign: isRtl ? 'right' : 'left' }}>{t.date}</label>
                  <input type="date" name="date" value={wasteEditForm.date} onChange={handleWasteEditChange} style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #d2d2d2', textAlign: isRtl ? 'right' : 'left' }} required />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#6d7175', marginBottom: 4, textAlign: isRtl ? 'right' : 'left' }}>{t.time}</label>
                  <input type="time" name="time" value={wasteEditForm.time} onChange={handleWasteEditChange} style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #d2d2d2', textAlign: isRtl ? 'right' : 'left' }} />
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#6d7175', marginBottom: 4, textAlign: isRtl ? 'right' : 'left' }}>{t.comment}</label>
                <textarea name="comment" value={wasteEditForm.comment} onChange={handleWasteEditChange} style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #d2d2d2', minHeight: 60, textAlign: isRtl ? 'right' : 'left' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                <button type="button" onClick={() => setEditWasteModal(false)} style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #d2d2d2', background: '#fff', cursor: 'pointer' }}>{t.cancel_btn}</button>
                <button type="submit" style={{ padding: '8px 16px', borderRadius: 6, background: '#1976d2', color: '#fff', border: 'none', cursor: 'pointer' }}>{t.save_changes}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Overview Modal */}
      {overviewOpen && (
        <div className="overlay-fade-in" style={{
          position: 'fixed', inset: 0, background: 'rgba(26, 28, 29, 0.55)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1200,
          backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)'
        }}>
          <div className="modal-fade-in" style={{
            width: '100vw', height: '100vh', background: '#fff',
            boxShadow: '0 28px 60px rgba(0,0,0,0.25)', overflow: 'hidden',
            display: 'flex', flexDirection: 'column', position: 'relative',
            direction: isRtl ? 'rtl' : 'ltr'
          }}>
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #ebebeb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: isRtl ? 'row-reverse' : 'row' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1a1c1d', textAlign: isRtl ? 'right' : 'left' }}>{t.product_overview}</h2>
                <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{t.material_flow_diagram_for} {product.product_name}</p>
              </div>
              <button onClick={() => setOverviewOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6d7175', padding: 4 }}>
                <svg width="24" height="24" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
              </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
              <ProductFlowDiagram
                product={product}
                wasteProducts={wasteProducts}
                dispatches={dispatches}
                downstreamProducts={downstreamProducts}
                currentUuid={product?.uuid}
                onShowGroup={handleShowGroupOverview}
              />
            </div>
          </div>
        </div>
      )}

      {deleteConfirm.open && (
        <DeleteConfirmation
          itemName={deleteConfirm.name}
          onConfirm={executeDelete}
          onCancel={() => setDeleteConfirm({ open: false, id: null, name: '' })}
        />
      )}

      {showExportConfirm && (
        <div className="overlay-fade-in" style={{
          position: 'fixed', inset: 0, background: 'rgba(26, 28, 29, 0.45)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1300,
          backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)'
        }}>
          <div className="modal-fade-in" style={{ width: 400, background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 20px 40px rgba(0,0,0,0.2)', direction: isRtl ? 'rtl' : 'ltr', textAlign: isRtl ? 'right' : 'left' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: 18, fontWeight: 700, color: '#1a1c1d' }}>{t.export_product_report}</h3>
            <p style={{ margin: '0 0 24px 0', fontSize: 14, color: '#6d7175', lineHeight: 1.5 }}>
              {t.export_product_report_msg_long}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
              <button onClick={() => setShowExportConfirm(false)} disabled={exportLoading} style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #d2d2d2', background: '#fff', color: '#1a1c1d', fontWeight: 600, fontSize: 13, cursor: exportLoading ? 'not-allowed' : 'pointer', opacity: exportLoading ? 0.6 : 1 }}>{t.cancel_btn}</button>
              <button onClick={handleExportPDF} disabled={exportLoading} style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: '#1976d2', color: '#fff', fontWeight: 600, fontSize: 13, cursor: exportLoading ? 'not-allowed' : 'pointer', boxShadow: '0 2px 4px rgba(25,118,210,0.2)', display: 'flex', alignItems: 'center', gap: 8, opacity: exportLoading ? 0.8 : 1, minWidth: 100, justifyContent: 'center', flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                {exportLoading ? <div className="dots-loading"><span></span><span></span><span></span></div> : t.start_export}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Group Overview Modal */}
      {groupOverviewOpen && groupOverview && (
        <div className="overlay-fade-in" style={{
          position: 'fixed', inset: 0, background: 'rgba(26,28,29,0.55)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1300, backdropFilter: 'blur(4px)'
        }}>
          <div className="modal-fade-in" style={{
            width: '100vw', height: '100vh', maxHeight: 'none', overflowY: 'auto',
            background: '#fff', padding: 28, boxShadow: '0 24px 50px rgba(0,0,0,0.2)',
            display: 'flex', flexDirection: 'column', direction: isRtl ? 'rtl' : 'ltr'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
              <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{t.group_overview}</h2>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6d7175' }}>
                  {t.group_overview_msg.replace('{count}', groupOverview.productIds?.length)}
                </p>
              </div>
              <button onClick={() => setGroupOverviewOpen(false)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#6d7175' }}>&times;</button>
            </div>

            {/* Flow Diagram */}
            <div style={{ marginBottom: 24, border: '1px solid #ebebeb', borderRadius: 12, overflow: 'visible', flexShrink: 0, minHeight: 550, textAlign: isRtl ? 'right' : 'left' }}>
              <div style={{ padding: '10px 16px', background: '#f9fafb', borderBottom: '1px solid #ebebeb', fontSize: 13, fontWeight: 600 }}>
                {t.group_production_flow}
              </div>
              <ProductFlowDiagram
                product={{
                  product_name: 'Group Batch',
                  used_raw_materials: groupOverview.used_raw_materials || [],
                  used_products: groupOverview.used_products || [],
                  product_quantiry: groupOverview.total_output_qty,
                  quantityProduced: groupOverview.total_ingredients_qty
                }}
                groupProducts={groupOverview.productIds || []}
                wasteProducts={groupOverview.wasteProductIds || []}
                currentUuid={uuid}
                onShowSimple={handleShowSimpleOverview}
              />
            </div>
          </div>
        </div>
      )}


    </>
  );
};

export default ProductDetail;
